import { Link, useParams } from "@tanstack/react-router";
import { Arrow } from "../components/Brand";
import { PageMeta } from "../components/PageMeta";
import { ProjectMedia } from "../components/ProjectMedia";
import { CTASection, TagList } from "../components/Sections";
import { clientProjects, projects } from "../data/site";
import { useI18n, useLocalizedContent } from "../i18n";

export function WorkDetailPage() {
  const { l, locale } = useI18n();
  const localizedProjects = useLocalizedContent(projects);
  const localizedClientProjects = useLocalizedContent(clientProjects);
  const { projectId } = useParams({ from: "/work/$projectId" });
  const project =
    localizedProjects.find((item) => item.slug === projectId) ??
    localizedProjects[0];
  const isClientProject = project.ownership === "client";
  const projectIndex = localizedClientProjects.findIndex(
    (item) => item.slug === project.slug,
  );
  const next =
    localizedClientProjects[
      (projectIndex + 1) % localizedClientProjects.length
    ];

  return (
    <>
      <PageMeta title={project.title} />
      <article>
        <header className="case-hero">
          <div className="container">
            <Link
              className="case-back"
              to={isClientProject ? "/work" : "/products"}
            >
              <Arrow direction="right" />
              {isClientProject
                ? l("返回客户案例", "Back to work")
                : l("返回产品", "Back to products")}
            </Link>
            <div className="case-hero__grid">
              <div>
                <span className="micro-label">
                  {project.client} · {project.industry}
                </span>
                <h1>{project.title}</h1>
                <p>{project.summary}</p>
                <TagList tags={project.tags} />
                {project.liveUrl && (
                  <a
                    className="text-link case-live-link"
                    href={project.liveUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {l("访问上线项目", "Visit live project")}{" "}
                    <Arrow direction="up-right" />
                  </a>
                )}
              </div>
              <div
                className={`case-hero__visual project-visual--${project.accent}`}
              >
                <ProjectMedia project={project} priority />
              </div>
            </div>
          </div>
        </header>

        <section className="case-facts">
          <div className="container case-facts__grid">
            <div>
              <span>{l("项目类型", "Project type")}</span>
              <strong>{project.projectType}</strong>
            </div>
            <div>
              <span>{l("交付范围", "Scope")}</span>
              <strong>{project.delivery}</strong>
            </div>
            <div>
              <span>{l("项目状态", "Status")}</span>
              <strong>{project.status}</strong>
            </div>
          </div>
        </section>

        <section className="section case-story">
          <div className="container case-story__grid">
            <aside>
              <span>01</span>
              <strong>{l("项目挑战", "Challenge")}</strong>
            </aside>
            <div>
              <h2>{project.challengeTitle}</h2>
              <p>{project.challenge}</p>
            </div>
            <aside>
              <span>02</span>
              <strong>{l("解决路径", "Approach")}</strong>
            </aside>
            <div>
              <h2>{project.approachTitle}</h2>
              <p>{project.approach}</p>
            </div>
          </div>
        </section>

        <section className="case-system dark-section">
          <div className="container">
            <p className="section-label section-label--inverse">
              {l("系统设计", "SYSTEM DESIGN")}
            </p>
            <h2>{project.systemTitle}</h2>
            <div className="case-flow">
              {project.flow.map((item, index) => (
                <div key={item}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <strong>{item}</strong>
                  {index < project.flow.length - 1 && <Arrow />}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section case-outcome">
          <div className="container case-outcome__grid">
            <p className="section-label">{l("项目成果", "OUTCOME")}</p>
            <blockquote>
              {locale === "zh" ? `“${project.result}”` : project.result}
            </blockquote>
            <p>{project.resultNote}</p>
          </div>
        </section>

        {isClientProject && next ? (
          <Link
            className="next-case"
            to="/work/$projectId"
            params={{ projectId: next.slug }}
          >
            <div className="container">
              <span>{l("下一个客户案例", "Next client case")}</span>
              <h2>{next.title}</h2>
              <div className="round-arrow">
                <Arrow direction="up-right" />
              </div>
            </div>
          </Link>
        ) : null}
      </article>
      <CTASection />
    </>
  );
}
